/* eslint-disable import/prefer-default-export */
import _ from 'lodash';
import getQuery from '../query';
import { accounts, market, publicKey, settings, user } from '../../api';
import { isClCard } from '../helpers';
import { walletCurrenciesTypes } from '../../screens/Accounts/constants';
import { handleError } from '../errorHandling';

const queryClient = getQuery();

export const RATES_QUERY_KEY = 'rates';
export const SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY = 'settings.supported_countries';
export const SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY = 'settings.supported_currencies';
export const SETTINGS_ALL_CURRENCIES = 'settings.all_currencies';
export const SETTINGS_US_STATES = 'settings.us.states';
export const SETTINGS_COUNTRY_SUBDIVISIONS = 'settings.country.subdivisions';
export const SETTINGS_ALL_QUERY_KEY = 'settings.all';
export const PASSWORD_PUBLIC_KEY = 'public.key.password';

export const NEW_SETTINGS_QUERY_ALL = 'settings.new.all.without.auth';

export const NEW_SETTINGS_QUERY_ALL_AUTH = 'settings.new.all.with.auth';

export const NEW_SETTINGS_QUERY_KEY_TOGGLE = 'setting.new.toggle';

export const NEW_SETTINGS_QUERY_BY_KEYS = 'setting.new.byKeys';

export const USER_REGION_SETTINGS_QUERY_KEY = 'settings.userRegionSettings';
export const USER_DETAILS_QUERY_KEY = 'user.details';
export const USER_PREFERENCES = 'user.preferences';
export const ACCOUNT_MAILING_ADDRESSES = 'account.mailing_addresses';
export const SETTINGS_TIER_STANDARD_ALL_KEY = 'settings.tier_standard_all';

export const USER_CARD_INFO = 'user.cardInfo';

export const USER_TIERS_RISK_ASSESSMENTS = 'user.tiers_risk_assessments';

queryClient.setQueryDefaults(RATES_QUERY_KEY, {
  queryFn: () => market.allRates(),
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY, {
  queryFn: () =>
    queryClient.getQueryData(SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY) ||
    settings.supportedCountries(),
  onSuccess: (result) => {
    const dataExists = queryClient.getQueryData(SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY);
    if (!dataExists) {
      queryClient.setQueryData(SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY, () => result);
    }
  },
});

queryClient.setQueryDefaults(SETTINGS_TIER_STANDARD_ALL_KEY, {
  queryFn: () =>
    queryClient.getQueryData(SETTINGS_TIER_STANDARD_ALL_KEY) || settings.tierStandards(),
  onSuccess: (result) => {
    const dataExists = queryClient.getQueryData(SETTINGS_TIER_STANDARD_ALL_KEY);
    if (!dataExists) {
      queryClient.setQueryData(SETTINGS_TIER_STANDARD_ALL_KEY, () => result);
    }
  },
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(SETTINGS_US_STATES, {
  queryFn: () => queryClient.getQueryData(SETTINGS_US_STATES) || settings.usStates(),
  onSuccess: (result) => {
    const dataExists = queryClient.getQueryData(SETTINGS_US_STATES);
    if (!dataExists) {
      queryClient.setQueryData(SETTINGS_US_STATES, () => result);
    }
  },
});

queryClient.setQueryDefaults(SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY, {
  queryFn: async ({ queryKey }) => {
    let currencies = queryClient.getQueryData(SETTINGS_ALL_CURRENCIES);

    if (!currencies) {
      const { currencies: _currencies } = await settings.supportedCurrencies();
      currencies = _currencies;
      queryClient.setQueryData(SETTINGS_ALL_CURRENCIES, () => _currencies);
    }

    if (queryKey[1] === walletCurrenciesTypes.CRYPTO) {
      const cryptoCurrencies = _.keyBy(
        currencies.filter(
          (c) => c.enabled === true && c.currency_type === walletCurrenciesTypes.CRYPTO
        ),
        queryKey[2] || 'currency_code'
      );
      return cryptoCurrencies;
    }

    if (queryKey[1] === walletCurrenciesTypes.FIAT) {
      const fiatCurrencies = _.keyBy(
        currencies.filter(
          (c) =>
            c.enabled === true &&
            c.currency_type.toLowerCase() === walletCurrenciesTypes.FIAT &&
            !isClCard(c.currency_code)
        ),
        queryKey[2] || 'currency_code'
      );

      return fiatCurrencies;
    }

    if (queryKey[1] === walletCurrenciesTypes.INTERNAL) {
      const internalCurrencies = _.keyBy(
        currencies.filter(
          (c) => c.enabled === true && c.currency_type === walletCurrenciesTypes.INTERNAL
        ),
        queryKey[2] || 'currency_code'
      );

      return internalCurrencies;
    }

    if (typeof queryKey[1] === 'object') {
      const filter = queryKey[1];
      const customFilter = _.keyBy(
        currencies.filter((c) => {
          let matchesFilter = c.enabled === true && !isClCard(c.currency_code);
          Object.keys(filter).forEach((filterKey) => {
            if (filterKey === 'currency_type') {
              // c.fiat_currency_peg is true when the currency is stable
              // we included the stable coins within the fiat ones.
              if (filter[filterKey] === walletCurrenciesTypes.FIAT) {
                matchesFilter =
                  matchesFilter &&
                  (c.fiat_currency_peg ||
                    c.currency_type.toLowerCase() === walletCurrenciesTypes.FIAT);
              } else {
                matchesFilter =
                  matchesFilter &&
                  !c.fiat_currency_peg &&
                  c.currency_type.toLowerCase() === walletCurrenciesTypes.CRYPTO;
              }
            } else {
              matchesFilter = matchesFilter && c[filterKey] === filter[filterKey];
            }
          });
          return matchesFilter;
        }),
        queryKey[2] || 'currency_code'
      );

      return customFilter;
    }

    const enabledCurrencies = _.keyBy(
      currencies.filter((c) => c.enabled === true && !isClCard(c.currency_code)),
      'currency_code'
    );
    return enabledCurrencies;
  },
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(USER_REGION_SETTINGS_QUERY_KEY, {
  queryFn: () =>
    queryClient.getQueryData(USER_REGION_SETTINGS_QUERY_KEY) || settings.userRegionSettings(),
  onSuccess: (result) => {
    const dataExists = queryClient.getQueryData(USER_REGION_SETTINGS_QUERY_KEY);
    if (!dataExists) {
      queryClient.setQueryData(USER_REGION_SETTINGS_QUERY_KEY, () => result);
    }
  },
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(SETTINGS_ALL_QUERY_KEY, {
  queryFn: () => queryClient.getQueryData(SETTINGS_ALL_QUERY_KEY) || settings.all(),
  onSuccess: (result) => {
    const dataExists = queryClient.getQueryData(SETTINGS_ALL_QUERY_KEY);
    if (!dataExists) {
      queryClient.setQueryData(SETTINGS_ALL_QUERY_KEY, () => result);
    }
  },
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(PASSWORD_PUBLIC_KEY, {
  queryFn: () => queryClient.getQueryData(PASSWORD_PUBLIC_KEY) || publicKey.password(),
  onSuccess: (result) => {
    const dataExists = queryClient.getQueryData(PASSWORD_PUBLIC_KEY);
    if (!dataExists) {
      queryClient.setQueryData(PASSWORD_PUBLIC_KEY, () => result);
    }
  },
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(NEW_SETTINGS_QUERY_BY_KEYS, {
  queryFn: ({ queryKey }) => settings.byKeys(queryKey[1]),
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(NEW_SETTINGS_QUERY_ALL, {
  queryFn: () => settings.newAll(),
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(NEW_SETTINGS_QUERY_ALL_AUTH, {
  queryFn: () => settings.newAll(),
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(NEW_SETTINGS_QUERY_KEY_TOGGLE, {
  queryFn: ({ queryKey }) =>
    queryClient.getQueryData(NEW_SETTINGS_QUERY_KEY_TOGGLE) || settings.byKeys(queryKey[1]),
  onSuccess: (result) => {
    const dataExists = queryClient.getQueryData(NEW_SETTINGS_QUERY_KEY_TOGGLE);
    if (!dataExists) {
      queryClient.setQueryData(NEW_SETTINGS_QUERY_KEY_TOGGLE, () => result);
    }
  },
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(USER_DETAILS_QUERY_KEY, {
  queryFn: () => queryClient.getQueryData(USER_DETAILS_QUERY_KEY) ?? user.details(),
  onSuccess: (result) => {
    const dataExists = queryClient.getQueryData(USER_DETAILS_QUERY_KEY);
    if (!dataExists) {
      queryClient.setQueryData(USER_DETAILS_QUERY_KEY, () => result);
    }
  },
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(USER_PREFERENCES, {
  queryFn: () => queryClient.getQueryData(USER_PREFERENCES) || { displayCurrency: 'EUR' },
});

queryClient.setQueryDefaults(ACCOUNT_MAILING_ADDRESSES, {
  queryFn: () => accounts.getMailingAddresses(),
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(USER_CARD_INFO, {
  queryFn: () => user.cardInfo(),
  onError: (error) => {
    handleError(error);
  },
});

queryClient.setQueryDefaults(USER_TIERS_RISK_ASSESSMENTS, {
  queryFn: () => user.tiersRishAssessments(),
  onError: (error) => {
    handleError(error);
  },
});
